/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaQuoteSheet.tsx
 * FILE_ID: QA-UI-QUOTE-SHEET-001
 * PURPOSE: Phone Quote sheet body — search plus the QAs box.
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ContextMenu, MobileSheet, useContextMenu } from '@quotech/ui'
import { qaHubRecordUrl } from '../../qa/qaCrossDoorLinks'
import type { QaInspectionSummary } from '../../qa/qaApi'
import { QaInspectionNavigator, type QaMenuOpenEvent } from './QaInspectionNavigator'
import { buildQaInspectionMenu, inspectionGroupChoices } from './qaInspectionMenu'
import { promptQaDuplicateCount } from './promptQaDuplicateCount'
import { useQaPaste } from './useQaPaste'
import type { QaWorkspaceController } from './useQaWorkspace'

export function QaQuoteSheet({
  open,
  onClose,
  workspace,
  jobId,
}: {
  open: boolean
  onClose: () => void
  workspace: QaWorkspaceController
  jobId: number
}) {
  const [query, setQuery] = useState('')
  const [menuInspection, setMenuInspection] = useState<QaInspectionSummary | null>(null)
  const [moveIds, setMoveIds] = useState<string[]>([])
  const [jobRequest, setJobRequest] = useState<QaInspectionSummary | null>(null)
  const [groupRequestIds, setGroupRequestIds] = useState<string[] | null>(null)
  const [renameRowId, setRenameRowId] = useState<string | null>(null)
  const menu = useContextMenu()
  const queryClient = useQueryClient()
  const pasted = useQaPaste({
    api: workspace.api,
    queryClient,
    jobId,
    setError: workspace.setError,
  })
  const duplicateCounted = async (ids: string[], count: number) => {
    for (const id of ids) await pasted.duplicateMany.mutateAsync({ id, count })
  }
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle
      ? workspace.inspections.filter((row) => row.inspection_name.toLowerCase().includes(needle))
      : workspace.inspections
  }, [query, workspace.inspections])
  const duplicateHere = (inspection: QaInspectionSummary, count = 1) => {
    void duplicateCounted([inspection.id], count)
  }
  const askDuplicate = (inspection: QaInspectionSummary) => {
    void promptQaDuplicateCount(1).then((count) => {
      if (count != null) void duplicateCounted([inspection.id], count)
    })
  }
  const openMenu = (inspection: QaInspectionSummary, event: QaMenuOpenEvent, ids: string[]) => {
    setMenuInspection(inspection)
    setMoveIds(ids)
    menu.openAt(event)
  }
  return (
    <MobileSheet open={open} onClose={onClose} title="Quote sheet" size="full">
      <div className="px-3 py-2">
        {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/sheets/QaPhoneScopeSheet.tsx (search input) */}
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search"
          className="min-h-[44px] min-w-0 w-full rounded-lg border border-border bg-background px-3 text-base"
        />
      </div>
      <QaInspectionNavigator
        inspections={shown}
        activeInspectionId={workspace.activeInspectionId}
        jobId={jobId}
        layout="sheet"
        onSelect={(id) => {
          workspace.selectInspection(id)
          onClose()
        }}
        onDuplicate={duplicateHere}
        onDuplicateToJob={(inspection, jobNumber) =>
          workspace.duplicateInspection.mutateAsync({ id: inspection.id, body: { job_id: jobNumber } })
        }
        onRenameSubmit={(inspection, name) => {
          workspace.selectInspection(inspection.id)
          workspace.renameInspection.mutate({ id: inspection.id, name })
        }}
        renamingId={
          workspace.renameInspection?.isPending ? workspace.renameInspection.variables?.id ?? null : null
        }
        renameRowId={renameRowId}
        onRenameRowStarted={() => setRenameRowId(null)}
        onDelete={(inspection) => {
          workspace.selectInspection(inspection.id)
          workspace.remove.mutate(inspection.id)
        }}
        onAssignGroup={(body) => workspace.assignInspectionGroup.mutate(body)}
        onDeleteSelected={async (ids) => {
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaWorkspace.ts (remove, duplicateInspection)
          for (const id of ids) await workspace.remove.mutateAsync(id)
        }}
        onDuplicateSelected={duplicateCounted}
        onOpenMenu={openMenu}
        jobRequest={jobRequest}
        onJobRequestClear={() => setJobRequest(null)}
        groupRequestIds={groupRequestIds}
        onGroupRequestClear={() => setGroupRequestIds(null)}
      />
      <ContextMenu
        {...menu.props}
        items={menuInspection ? buildQaInspectionMenu({
          groups: inspectionGroupChoices(workspace.inspections),
          onOpenHub: () => window.open(qaHubRecordUrl(menuInspection.id), '_blank', 'noopener,noreferrer'),
          onRename: () => setRenameRowId(menuInspection.id),
          onDuplicate: () => askDuplicate(menuInspection),
          onDuplicateToJob: () => {
            setGroupRequestIds(null)
            setJobRequest(menuInspection)
          },
          onMove: (groupId) => {
            workspace.assignInspectionGroup.mutate({
              inspection_ids: moveIds,
              job_id: String(jobId),
              ...(groupId ? { group_id: groupId } : {}),
            })
          },
          onNewGroup: () => {
            setJobRequest(null)
            setGroupRequestIds(moveIds)
          },
          onDelete: () => {
            workspace.selectInspection(menuInspection.id)
            workspace.remove.mutate(menuInspection.id)
          },
        }) : []}
      />
    </MobileSheet>
  )
}
